import React, { useState, useEffect, useRef } from 'react';
import { FiPlus } from 'react-icons/fi';

/**
 * AssigneePicker
 *
 * Fixes the "empty strip" bug: when assignees were pre-filled but the dropdown
 * had never been opened, the avatar group rendered nothing because the user
 * list wasn't fetched yet. Now:
 *  - Users are fetched on mount whenever assignees.length > 0
 *  - Users are fetched lazily when opened in all cases
 *  - While users are loading, the button shows a safe "Assigned (N)" fallback
 *  - selectedIds stays in sync with the assignees prop
 */
const AssigneePicker = ({ assignees = [], onChange, token }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedIds, setSelectedIds] = useState(assignees || []);
  const dropdownRef = useRef(null);

  // Keep selection in sync if the parent updates the assignees prop
  useEffect(() => {
    setSelectedIds(assignees || []);
  }, [assignees]);

  // Fetch users eagerly when there are assignees we need to render, and
  // also when the dropdown is opened.
  const fetchUsers = async () => {
    if (loadingUsers) return;
    setLoadingUsers(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
        setUsersLoaded(true);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Eager fetch if we have assignees to render
  useEffect(() => {
    if ((assignees || []).length > 0 && !usersLoaded && !loadingUsers && token) {
      fetchUsers();
    }
    // eslint-disable-next-line
  }, [assignees, token]);

  // Lazy fetch when opening
  useEffect(() => {
    if (isOpen && !usersLoaded && !loadingUsers && token) {
      fetchUsers();
    }
    // eslint-disable-next-line
  }, [isOpen]);

  // Outside click to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleUser = (userId) => {
    const newSelection = selectedIds.includes(userId)
      ? selectedIds.filter((id) => id !== userId)
      : [...selectedIds, userId];
    setSelectedIds(newSelection);
    onChange(newSelection);
  };

  const getAssignedUsers = () => users.filter((u) => selectedIds.includes(u.id));
  const assignedUsers = getAssignedUsers();
  const hasAssignees = (selectedIds || []).length > 0;

  return (
    <div ref={dropdownRef} style={{ position: 'relative', width: '100%' }}>
      <button
        type="button"
        className="assignee-picker-btn"
        onClick={() => setIsOpen((v) => !v)}
        data-testid="assignee-picker-button"
      >
        {!hasAssignees ? (
          <>
            <FiPlus size={14} />
            <span>Assign</span>
          </>
        ) : assignedUsers.length > 0 ? (
          <div className="avatar-group" data-testid="assignee-avatar-group">
            {assignedUsers.slice(0, 3).map((user) => (
              <div key={user.id} className="avatar avatar-sm" title={user.name}>
                {user.name?.charAt(0).toUpperCase() || 'U'}
              </div>
            ))}
            {selectedIds.length > 3 && (
              <span style={{ fontSize: '11px', marginLeft: '4px' }}>+{selectedIds.length - 3}</span>
            )}
          </div>
        ) : (
          // Fallback while users list is loading: never render an empty strip
          <span
            className="assignee-fallback"
            style={{ fontSize: '12px', color: 'var(--text-secondary, #6b7280)', fontWeight: 500 }}
            data-testid="assignee-fallback"
          >
            Assigned ({selectedIds.length})
          </span>
        )}
      </button>
      {isOpen && (
        <div className="dropdown" style={{ top: '100%', left: 0, marginTop: '4px', minWidth: '220px', zIndex: 1000 }}>
          <div style={{ padding: '8px 12px', fontWeight: 600, fontSize: '13px', color: 'var(--text-secondary)' }}>
            Select Team Members
          </div>
          {loadingUsers && users.length === 0 ? (
            <div className="dropdown-item" style={{ color: 'var(--text-secondary)', fontSize: '13px', cursor: 'default' }}>
              Loading…
            </div>
          ) : (
            users.map((user) => (
              <div
                key={user.id}
                className="dropdown-item"
                onClick={() => toggleUser(user.id)}
                data-testid="assignee-option"
              >
                <input
                  type="checkbox"
                  checked={selectedIds.includes(user.id)}
                  onChange={() => {}}
                  style={{ marginRight: '8px' }}
                />
                <div className="avatar avatar-sm" style={{ marginRight: '8px' }}>
                  {user.name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 500 }}>{user.name}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{user.role}</div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default AssigneePicker;
